import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import Nutrition from './Nutrition';
import Tag from './Tag';
import FullscreenModal from './FullscreenModal';
import { getRecipeField, getEnumTranslation, getRecipeTypeIcon, formatDate, getUITranslation } from './utils';

const RecipeDetails = ({ recipe, lang }) => {
    const navigate = useNavigate();
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [fullscreenIndex, setFullscreenIndex] = useState(0);
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [recipe]);

    const handleLanguageChange = (newLang) => {
        navigate(`/recipe-book/${newLang}/${recipe.id}`);
    };

    const openFullscreen = (imageUrl, index) => {
        setFullscreenImage(imageUrl);
        setFullscreenIndex(index);
    };

    const closeFullscreen = () => {
        setFullscreenImage(null);
        setFullscreenIndex(0);
    };
    
    return (
        <div>
            <div className="nav-controls">
                <Link className="back-btn" to={`/recipe-book/${lang}`}>&larr; {getUITranslation('back', lang)}</Link>
                <LanguageSelector lang={lang} setLang={handleLanguageChange} />
            </div>
            <div className="recipe-details">
                <div className="recipe-details-header">
                    <img src={recipe.image} alt={getRecipeField(recipe, 'title', lang)} className="recipe-details-img" />
                    <div className="recipe-details-meta">
                        <h2 className="recipe-title-fancy">{getRecipeField(recipe, 'title', lang)}</h2>
                        <div className="recipe-times">
                            {recipe.prepTime && <span>{getUITranslation('prep', lang)}: <b>{getRecipeField(recipe, 'prepTime', lang)}</b></span>}
                            {recipe.cookTime && <span>{getUITranslation('cook', lang)}: <b>{getRecipeField(recipe, 'cookTime', lang)}</b></span>}
                        </div>
                        <p>{getRecipeField(recipe, 'description', lang)}</p>
                        <div className="recipe-card-tags">
                            {recipe.author && <Tag>{getEnumTranslation(recipe.author, lang)}</Tag>}
                            {recipe.date && <Tag>{formatDate(recipe.date, lang)}</Tag>}
                            {recipe.country && <Tag>{getEnumTranslation(recipe.country, lang)}</Tag>}
                            {recipe.cuisine && <Tag>{getEnumTranslation(recipe.cuisine, lang)}</Tag>}
                            {recipe.type && <Tag>{getRecipeTypeIcon(recipe.type)} {getEnumTranslation(recipe.type, lang)}</Tag>}
                        </div>
                        <Nutrition nutrition={recipe.nutrition} recipeType={recipe.type} lang={lang} />
                    </div>
                </div>
                <div className="recipe-details-body">
                    <div className="columns-vertical">
                        <div className="ingredients-col">
                            <h4>{getUITranslation('ingredients', lang)}</h4>
                            <ul className="ingredients-list">
                                {(getRecipeField(recipe, 'ingredients', lang) || []).map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                        <div className="instructions-col">
                            <h4>{getUITranslation('instructions', lang)}</h4>
                            <ol className="instructions-list">
                                {(getRecipeField(recipe, 'instructions', lang) || []).map((item, i) => <li key={i}>{item}</li>)}
                            </ol>
                        </div>
                        <div className="materials-col">
                            <h4>{getUITranslation('materials', lang)}</h4>
                            <ul>
                                {(recipe.materials || []).map((materialEnum, i) => <li key={i}>{getEnumTranslation(materialEnum, lang)}</li>)}
                            </ul>
                        </div>
                        {recipe.youtube && (
                            <div className="video-col">
                                <h4>{getUITranslation('video', lang)}</h4>
                                <div className="video-wrapper">
                                    <iframe width="100%" height="250" src={recipe.youtube} title="YouTube video" frameBorder="0" allowFullScreen></iframe>
                                </div>
                            </div>
                        )}
                        {(recipe.photos || []).length > 0 && (
                            <div className="gallery-col">
                                <h4>{getUITranslation('gallery', lang)}</h4>
                                <div className="photos-gallery">
                                    {(recipe.photos || []).map((url, i) => (
                                        <img 
                                            key={i} 
                                            src={url} 
                                            alt="Recipe step" 
                                            className="gallery-img" 
                                            onClick={() => openFullscreen(url, i)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Fullscreen Image Modal */}
            <FullscreenModal
                isOpen={!!fullscreenImage}
                imageUrl={fullscreenImage}
                images={recipe.photos}
                initialIndex={fullscreenIndex}
                onClose={closeFullscreen}
                altText={getRecipeField(recipe, 'title', lang)}
            />
        </div>
    );
};

export default RecipeDetails; 